import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import type { FormButtonSettingsData } from '@sites/entity-core';

import { ERadioGroupType } from '@ui/sp-radio-group';
import type { CustomButtonEventPayload } from '@components/sp-custom-button';
import { ButtonMode } from '@components/sp-custom-button';
import type { ButtonAdditionalEffectsSettings } from '@components/sp-button-additional-effects-settings/type/button-additional-effects-settings.type';
import type { ButtonIcon } from '@components/sp-button-icon/type/button-icon.type';
import type { ShadowSettings } from '@common/types/shadow-settings.type';
import { CSS_RESTRICTIONS } from '@common/constants';
import { isEmptyButtonLabel } from '@common/helpers/forms';
import { EButtonStyleType, EButtonWidthType } from '@common/enums';

import { ThemeStateService } from '@sites/site-theme/src/state';

import { SpRadioGroupComponent } from '@ui/sp-radio-group/src/sp-radio-group.component';
import { SpCustomButtonComponent } from '@components/sp-custom-button/sp-custom-button.component';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpButtonIconComponent } from '@components/sp-button-icon/sp-button-icon.component';
import { SpButtonAdditionalEffectsSettingsComponent } from '../sp-button-additional-effects-settings/sp-button-additional-effects-settings.component';
import { SpShadowSettingsComponent } from '../sp-shadow-settings/sp-shadow-settings.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../ui/sp-switch/src/sp-switch.component';

/**
 * Signal-form editor for the form widget's submit-button settings — the
 * signal twin of the FormGroup-based `sp-form-button-settings`. Mirrors that
 * template (label / style+size / custom-button block / width incl. the
 * horizontal-form variant / icon / effects / shadow) but follows the
 * value-in (`FormButtonSettingsData` from `@sites/entity-core`) /
 * `valueChangeEvent`-out contract of the other `*-signal-settings` twins.
 */
@Component({
    selector: 'sp-form-button-settings-signal',
    templateUrl: './sp-form-button-settings-signal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
        SpRadioGroupComponent,
        SpCustomButtonComponent,
        SpInputNumComponent,
        SpButtonIconComponent,
        SpButtonAdditionalEffectsSettingsComponent,
        SpShadowSettingsComponent,
        SpSwitchComponent,
    ],
})
export class SpFormButtonSettingsSignalComponent {
    private readonly themeState = inject(ThemeStateService);

    /** Snapshot of the parent signal-form's button-settings value. */
    public readonly settings = input<FormButtonSettingsData | null>(null);
    public readonly isHorizontalForm = input<boolean>(false);

    public readonly valueChangeEvent = output<FormButtonSettingsData>();

    public readonly CSS_RESTRICTIONS = CSS_RESTRICTIONS;
    public readonly EButtonWidthType = EButtonWidthType;
    public readonly EButtonStyleType = EButtonStyleType;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly ButtonMode = ButtonMode;

    public readonly selectedThemeColors = this.themeState.selectedColors;

    private readonly model = signal<FormButtonSettingsData | null>(null);

    public readonly label = computed(() => this.model()?.label ?? '');
    public readonly styleType = computed(() => this.model()?.styleType);
    public readonly size = computed(() => this.model()?.size);
    public readonly widthType = computed(() => this.model()?.widthType);
    public readonly horizontalFormWidthType = computed(() => this.model()?.horizontalFormWidthType);
    public readonly widthValue = computed(() => this.model()?.widthValue);
    public readonly buttonBase = computed(() => this.model()?.buttonBase);
    public readonly buttonHover = computed(() => this.model()?.buttonHover);
    public readonly buttonIcon = computed(() => this.model()?.buttonIcon);
    public readonly additionalEffects = computed(() => this.model()?.additionalEffects);
    public readonly isIndependentCorners = computed(() => Boolean(this.model()?.isIndependentCorners));
    public readonly advancedRadius = computed(() => this.model()?.advancedRadius);
    public readonly advancedBorderSettings = computed(() => this.model()?.advancedBorderSettings);
    public readonly isShadow = computed(() => Boolean(this.model()?.isShadow));
    public readonly shadowSettings = computed(() => this.model()?.shadowSettings);
    public readonly isCustomStyle = computed(() => this.styleType() === EButtonStyleType.custom);

    /** Blank button text is invalid — hosts gate Save on the same predicate. */
    public readonly isEmptyLabel = computed(() => isEmptyButtonLabel(this.label()));

    constructor() {
        effect(() => {
            const incoming = this.settings();
            if (!incoming) return;
            this.model.set(incoming);
        });
    }

    // --- Setters (template event handlers) ---

    public onLabelChange(event: Event): void {
        this.patch({ label: (event.target as HTMLInputElement).value });
    }

    public onStyleTypeChange(event: Event): void {
        this.patch({ styleType: (event.target as HTMLSelectElement).value as FormButtonSettingsData['styleType'] });
    }

    public setSize(value: string): void {
        this.patch({ size: value as FormButtonSettingsData['size'] });
    }

    public onWidthTypeChange(event: Event): void {
        this.patch({ widthType: (event.target as HTMLSelectElement).value as FormButtonSettingsData['widthType'] });
    }

    public onHorizontalFormWidthTypeChange(event: Event): void {
        this.patch({
            horizontalFormWidthType: (event.target as HTMLSelectElement)
                .value as FormButtonSettingsData['horizontalFormWidthType'],
        });
    }

    public setWidthValue(value: number): void {
        this.patch({ widthValue: value });
    }

    public customButtonChangeHandler(changes: CustomButtonEventPayload): void {
        const patch: Partial<FormButtonSettingsData> = {
            buttonBase: changes.base as unknown as FormButtonSettingsData['buttonBase'],
            buttonHover: changes.hover as unknown as FormButtonSettingsData['buttonHover'],
        };

        if (changes?.isIndependentCorners !== undefined) {
            patch.isIndependentCorners = changes.isIndependentCorners;
        }
        if (changes?.radiusSettings) {
            patch.advancedRadius = changes.radiusSettings as FormButtonSettingsData['advancedRadius'];
        }
        if (changes?.advancedBorderSettings) {
            patch.advancedBorderSettings =
                changes.advancedBorderSettings as unknown as FormButtonSettingsData['advancedBorderSettings'];
        }

        this.patch(patch);
    }

    public buttonIconChangeHandler(changes: ButtonIcon): void {
        this.patch({ buttonIcon: changes as unknown as FormButtonSettingsData['buttonIcon'] });
    }

    public additionalEffectsChangeHandler(changes: ButtonAdditionalEffectsSettings): void {
        this.patch({ additionalEffects: changes as unknown as FormButtonSettingsData['additionalEffects'] });
    }

    public setShadow(value: boolean): void {
        this.patch({ isShadow: value });
    }

    public setShadowSettings(settings: ShadowSettings): void {
        this.patch({ shadowSettings: settings });
    }

    private patch(patch: Partial<FormButtonSettingsData>): void {
        const current = this.model();
        if (!current) return;
        const next = { ...current, ...patch };
        this.model.set(next);
        this.valueChangeEvent.emit(next);
    }
}
